import { Icon } from "@iconify/react";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getVapidPublicKey,
  type PushSubscriptionData,
  unsubscribeFromPushNotifications as removeSubscriptionFromBackend,
  subscribeToPushNotifications as sendSubscriptionToBackend,
} from "../../../../api/eventnotifier/push";
import { Loader } from "../../../../components/widgets/Loader";
import { useApoAuth } from "../../../../hooks";
import jssVariables from "../../../../jss_setup";
import { TranslationContext } from "../../../../umbilical_tools";
import {
  getPushSubscription,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../../../../utils/serviceWorkerRegistration";

/**
 * Helper function to convert ArrayBuffer to base64 URL-safe string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

export function PushNotificationsCard() {
  const { user, source_entity, getIdTokenClaims, isAuthenticated } = useApoAuth();
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const username = user?.email || "";

  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>("default");
  const [isSupported, setIsSupported] = useState(false);

  // Check initial status
  useEffect(() => {
    const checkStatus = async () => {
      // Check if notifications are supported
      const notificationsSupported = "Notification" in window && "serviceWorker" in navigator;
      setIsSupported(notificationsSupported);

      if (!notificationsSupported) {
        return;
      }

      // Check permission status
      const permission = Notification.permission;
      setPermissionStatus(permission);

      // Check if there's an active subscription
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await getPushSubscription(registration);
        setIsEnabled(!!subscription && permission === "granted");
      } catch (error) {
        console.error("[PushNotificationsCard] Error checking subscription:", error);
        setIsEnabled(false);
      }
    };

    checkStatus();
  }, []);

  const handleToggle = async () => {
    if (!isSupported) {
      toast.warning(t("pushNotificationsNotSupported"));
      return;
    }

    if (!username || !source_entity || !isAuthenticated) {
      toast.error(t("userInformationMissing"));
      return;
    }

    setIsLoading(true);

    try {
      if (isEnabled) {
        // Disable push notifications
        await disablePushNotifications();
      } else {
        // Enable push notifications
        await enablePushNotifications();
      }
    } catch (error) {
      console.error("[PushNotificationsCard] Error toggling push notifications:", error);
      toast.error(t("failedToUpdatePushNotifications"));
    } finally {
      setIsLoading(false);
    }
  };

  const enablePushNotifications = async () => {
    try {
      // Get auth token
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Failed to get auth token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error("Failed to register service worker");
      }

      // Request notification permission
      const permission = await requestNotificationPermission();
      if (permission !== "granted") {
        if (permission === "denied") {
          toast.error(t("notificationPermissionDenied"));
        } else {
          toast.info(t("notificationPermissionRequired"));
        }
        setPermissionStatus(permission);
        return;
      }

      setPermissionStatus("granted");

      // Get VAPID public key
      const publicKey = await getVapidPublicKey();
      if (!publicKey) {
        throw new Error("Failed to get VAPID public key");
      }

      // Check if already subscribed
      let subscription = await getPushSubscription(registration);
      if (!subscription) {
        // Subscribe to push notifications
        subscription = await subscribeToPushNotifications(registration, publicKey);
        if (!subscription) {
          throw new Error("Failed to subscribe to push notifications");
        }
      }

      // Convert subscription to format expected by backend
      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
          auth: arrayBufferToBase64(subscription.getKey("auth")),
        },
        expirationTime: subscription.expirationTime,
      };

      // Send subscription to backend
      const success = await sendSubscriptionToBackend(
        username,
        source_entity,
        subscriptionData,
        token,
      );

      if (success) {
        setIsEnabled(true);
        toast.success(t("pushNotificationsEnabled"));
      } else {
        throw new Error("Failed to send subscription to backend");
      }
    } catch (error) {
      console.error("[PushNotificationsCard] Error enabling push notifications:", error);
      throw error;
    }
  };

  const disablePushNotifications = async () => {
    try {
      // Get auth token
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Failed to get auth token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;

      const registration = await navigator.serviceWorker.ready;
      const subscription = await getPushSubscription(registration);

      if (subscription) {
        // Unsubscribe from browser
        await unsubscribeFromPushNotifications(registration);

        // Remove from backend
        await removeSubscriptionFromBackend(source_entity, username, token, subscription.endpoint);
      }

      setIsEnabled(false);
      toast.success(t("pushNotificationsDisabled"));
    } catch (error) {
      console.error("[PushNotificationsCard] Error disabling push notifications:", error);
      throw error;
    }
  };

  const getStatusText = () => {
    if (!isSupported) {
      return t("notSupported");
    }
    if (permissionStatus === "denied") {
      return t("permissionDenied");
    }
    if (permissionStatus === "default") {
      return t("permissionNotRequested");
    }
    return isEnabled ? t("enabled") : t("disabled");
  };

  const getStatusTagStyle = () => {
    if (!isSupported || permissionStatus === "denied") {
      return "bg-gray-200 text-gray-700";
    }
    if (permissionStatus === "default") {
      return "bg-yellow-100 text-yellow-800";
    }
    return isEnabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700";
  };

  return (
    <div className="PushNotificationsCard grid gap-3 p-3 bg-[#F9F9F9] rounded-sm">
      <style>
        {`
          .PushNotificationsCard .peer:focus ~ div {
            box-shadow: 0 0 0 4px ${jssVariables.colors.base_color}4D;
          }
          .PushNotificationsCard .peer ~ div::after {
            border-color: ${jssVariables.colors.base_color}80;
          }
        `}
      </style>
      <div className="grid grid-cols-[1fr_auto] items-center mb-3">
        <div className="text-lg font-medium">{t("pushNotifications")}</div>
      </div>

      {isLoading ? (
        <div className="grid justify-center items-center py-8">
          <Loader />
        </div>
      ) : (
        <div className="grid gap-3">
          <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
            <Icon
              icon="mdi:bell-outline"
              className="text-2xl"
              style={{ color: jssVariables.colors.base_color }}
            />
            <div className="grid gap-1">
              <p className="text-base">{t("pushNotificationsDescription")}</p>
              <span
                className={`inline-block px-2 py-1 text-xs font-semibold rounded ${getStatusTagStyle()} w-fit`}
              >
                {getStatusText()}
              </span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={handleToggle}
                disabled={isLoading || !isSupported || permissionStatus === "denied"}
                className="sr-only peer"
              />
              <div
                className={[
                  "w-11",
                  "h-6",
                  "rounded-full",
                  "peer",
                  "peer-focus:outline-none",
                  "peer-checked:after:translate-x-full",
                  "peer-checked:after:border-white",
                  "after:content-['']",
                  "after:absolute",
                  "after:top-[2px]",
                  "after:left-[2px]",
                  "after:bg-white",
                  "after:border",
                  "after:rounded-full",
                  "after:h-5",
                  "after:w-5",
                  "after:transition-all",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  backgroundColor: isEnabled ? jssVariables.colors.base_color : "#d1d5db",
                }}
              />
            </label>
          </div>

          {permissionStatus === "denied" && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
              <p className="text-sm text-yellow-800">{t("pushNotificationsDeniedMessage")}</p>
            </div>
          )}

          {!isSupported && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-sm">
              <p className="text-sm text-gray-600">{t("pushNotificationsNotSupported")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
